import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { db } from '../config/firebase';
import {
  collection,
  onSnapshot,
  addDoc,
  updateDoc,
  doc,
  query,
  where,
  getDocs,
  Timestamp,
} from 'firebase/firestore';
import { logger } from '../utils/logger';
import { COLLECTIONS } from '../config/constants';

// Referans durumları
export type ReferralStatus = 'pending' | 'registered' | 'first_order' | 'completed' | 'expired';

// Referans tipi
export interface Referral {
  id: string;
  referrerId: string;           // Davet eden kullanıcı
  referrerName: string;         // Davet edenin adı
  referrerEmail: string;        // Davet edenin e-postası
  referredEmail: string;        // Davet edilenin e-postası
  referredId?: string;          // Davet edilen kayıt olduktan sonra
  referredName?: string;        // Davet edilenin adı
  status: ReferralStatus;
  referralCode: string;         // Benzersiz referans kodu
  rewardAmount: number;         // Ödül tutarı (TL)
  referrerRewarded: boolean;    // Davet eden ödüllendirildi mi
  referredRewarded: boolean;    // Davet edilen ödüllendirildi mi
  createdAt: string;
  registeredAt?: string;        // Kayıt tarihi
  firstOrderAt?: string;        // İlk sipariş tarihi
  completedAt?: string;         // Tamamlanma tarihi
  expiresAt: string;            // Son geçerlilik tarihi
}

// Referans istatistikleri
export interface ReferralStats {
  totalReferrals: number;       // Toplam davet
  pendingReferrals: number;     // Bekleyen davetler
  completedReferrals: number;   // Tamamlanan davetler
  totalEarnings: number;        // Toplam kazanç
  availableBalance: number;     // Kullanılabilir bakiye
}

// Referans ayarları
export const REFERRAL_CONFIG = {
  REFERRER_REWARD: 25,          // Davet eden kazancı (TL)
  REFERRED_REWARD: 15,          // Davet edilen kazancı (TL)
  EXPIRY_DAYS: 30,              // Davet geçerlilik süresi (gün)
  MIN_ORDER_AMOUNT: 50,         // Minimum sipariş tutarı (TL)
};

// Context tipi
interface ReferralContextType {
  referrals: Referral[];
  stats: ReferralStats;
  myReferralCode: string | null;
  loading: boolean;
  
  // Davet işlemleri
  sendInvite: (email: string) => Promise<{ success: boolean; message: string }>;
  applyReferralCode: (code: string) => Promise<{ success: boolean; message: string }>;
  
  // Referans kodu oluşturma
  generateReferralCode: () => Promise<string>;
  
  // Ödül kullanma
  useReferralBalance: (amount: number) => Promise<boolean>;
  
  // Admin fonksiyonları
  getReferralByCode: (code: string) => Promise<Referral | null>;
  markReferralCompleted: (referralId: string) => Promise<void>;
}

const ReferralContext = createContext<ReferralContextType | undefined>(undefined);

export const useReferrals = () => {
  const context = useContext(ReferralContext);
  if (!context) {
    throw new Error('useReferrals must be used within a ReferralProvider');
  }
  return context;
};

interface ReferralProviderProps {
  children: ReactNode;
}

// Benzersiz referans kodu oluştur
const generateUniqueCode = (userId: string): string => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Karışıklık yaratabilecek karakterler çıkarıldı (I, O, 0, 1)
  const userPart = userId.slice(-4).toUpperCase();
  let randomPart = '';
  for (let i = 0; i < 4; i++) {
    randomPart += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return `ASIL${userPart}${randomPart}`;
};

export const ReferralProvider: React.FC<ReferralProviderProps> = ({ children }) => {
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [myReferralCode, setMyReferralCode] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const { currentUser } = useAuth();

  // İstatistikleri hesapla
  const stats = useMemo((): ReferralStats => {
    const myReferrals = referrals.filter(r => r.referrerId === currentUser?.uid);
    
    return {
      totalReferrals: myReferrals.length,
      pendingReferrals: myReferrals.filter(r => r.status === 'pending' || r.status === 'registered').length,
      completedReferrals: myReferrals.filter(r => r.status === 'completed').length,
      totalEarnings: myReferrals.filter(r => r.referrerRewarded).reduce((sum, r) => sum + r.rewardAmount, 0),
      availableBalance: myReferrals.filter(r => r.referrerRewarded).reduce((sum, r) => sum + r.rewardAmount, 0), // TODO: Kullanılan bakiyeyi çıkar
    };
  }, [referrals, currentUser?.uid]);

  // Kullanıcının referanslarını dinle
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!currentUser) {
      setReferrals([]);
      setMyReferralCode(null);
      setLoading(false);
      return;
    }

    setLoading(true);

    // Kullanıcının gönderdiği ve aldığı referansları dinle
    const q = query(
      collection(db, COLLECTIONS.REFERRALS),
      where('referrerId', '==', currentUser.uid)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const list: Referral[] = snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
          createdAt: d.data().createdAt?.toDate?.()?.toISOString() || d.data().createdAt,
          registeredAt: d.data().registeredAt?.toDate?.()?.toISOString() || d.data().registeredAt,
          firstOrderAt: d.data().firstOrderAt?.toDate?.()?.toISOString() || d.data().firstOrderAt,
          completedAt: d.data().completedAt?.toDate?.()?.toISOString() || d.data().completedAt,
          expiresAt: d.data().expiresAt?.toDate?.()?.toISOString() || d.data().expiresAt,
        } as Referral));
        
        setReferrals(list);
        setLoading(false);
      },
      (error) => {
        logger.error('Referans dinleme hatası:', error);
        setReferrals([]);
        setLoading(false);
      }
    );

    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.uid]);

  // Kullanıcının referans kodunu al veya oluştur
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const fetchOrCreateCode = async () => {
      if (!currentUser) return;

      try {
        // Önce mevcut kodu kontrol et
        const userDoc = await getDocs(
          query(collection(db, COLLECTIONS.REFERRAL_CODES), where('userId', '==', currentUser.uid))
        );

        if (!userDoc.empty) {
          setMyReferralCode(userDoc.docs[0].data().code);
        } else {
          // Yeni kod oluştur
          const newCode = generateUniqueCode(currentUser.uid);
          await addDoc(collection(db, COLLECTIONS.REFERRAL_CODES), {
            userId: currentUser.uid,
            code: newCode,
            createdAt: Timestamp.now(),
          });
          setMyReferralCode(newCode);
        }
      } catch (error) {
        logger.error('Referans kodu alma/oluşturma hatası:', error);
      }
    };

    fetchOrCreateCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.uid]);

  // Davet gönder
  const sendInvite = useCallback(async (email: string): Promise<{ success: boolean; message: string }> => {
    if (!currentUser || !myReferralCode) {
      return { success: false, message: 'Giriş yapmalısınız' };
    }

    // E-posta validasyonu
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return { success: false, message: 'Geçerli bir e-posta adresi girin' };
    }

    // Kendi kendine davet kontrolü
    if (email.toLowerCase() === currentUser.email?.toLowerCase()) {
      return { success: false, message: 'Kendinizi davet edemezsiniz' };
    }

    try {
      // Bu e-postaya daha önce davet gönderilmiş mi?
      const existingInvite = await getDocs(
        query(
          collection(db, COLLECTIONS.REFERRALS),
          where('referrerId', '==', currentUser.uid),
          where('referredEmail', '==', email.toLowerCase())
        )
      );

      if (!existingInvite.empty) {
        return { success: false, message: 'Bu kişiye zaten davet göndermişsiniz' };
      }

      // Yeni davet oluştur
      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + REFERRAL_CONFIG.EXPIRY_DAYS);

      await addDoc(collection(db, COLLECTIONS.REFERRALS), {
        referrerId: currentUser.uid,
        referrerName: currentUser.displayName || 'Müşteri',
        referrerEmail: currentUser.email,
        referredEmail: email.toLowerCase(),
        status: 'pending',
        referralCode: myReferralCode,
        rewardAmount: REFERRAL_CONFIG.REFERRER_REWARD,
        referrerRewarded: false,
        referredRewarded: false,
        createdAt: Timestamp.now(),
        expiresAt: Timestamp.fromDate(expiryDate),
      });

      // TODO: E-posta gönder (Firebase Cloud Functions ile)

      return { success: true, message: 'Davet başarıyla gönderildi!' };
    } catch (error) {
      logger.error('Davet gönderme hatası:', error);
      return { success: false, message: 'Davet gönderilemedi. Lütfen tekrar deneyin.' };
    }
  }, [currentUser, myReferralCode]);

  // Referans kodu uygula
  const applyReferralCode = useCallback(async (code: string): Promise<{ success: boolean; message: string }> => {
    if (!currentUser) {
      return { success: false, message: 'Giriş yapmalısınız' };
    }

    try {
      // Kodu bul
      const codeDoc = await getDocs(
        query(collection(db, COLLECTIONS.REFERRAL_CODES), where('code', '==', code.toUpperCase()))
      );

      if (codeDoc.empty) {
        return { success: false, message: 'Geçersiz referans kodu' };
      }

      const referrerId = codeDoc.docs[0].data().userId;

      // Kendi kodunu kullanma kontrolü
      if (referrerId === currentUser.uid) {
        return { success: false, message: 'Kendi referans kodunuzu kullanamazsınız' };
      }

      // Daha önce referans kullanılmış mı?
      const existingReferral = await getDocs(
        query(
          collection(db, COLLECTIONS.REFERRALS),
          where('referredId', '==', currentUser.uid)
        )
      );

      if (!existingReferral.empty) {
        return { success: false, message: 'Daha önce bir referans kodu kullanmışsınız' };
      }

      // Bu kullanıcı için bekleyen davet var mı?
      const pendingInvite = await getDocs(
        query(
          collection(db, COLLECTIONS.REFERRALS),
          where('referredEmail', '==', currentUser.email?.toLowerCase()),
          where('status', '==', 'pending')
        )
      );

      if (!pendingInvite.empty) {
        // Mevcut daveti güncelle
        const inviteDoc = pendingInvite.docs[0];
        await updateDoc(doc(db, COLLECTIONS.REFERRALS, inviteDoc.id), {
          referredId: currentUser.uid,
          referredName: currentUser.displayName || 'Müşteri',
          status: 'registered',
          registeredAt: Timestamp.now(),
        });
      } else {
        // Yeni referans oluştur
        const referrerDoc = await getDocs(
          query(collection(db, COLLECTIONS.USERS), where('uid', '==', referrerId))
        );
        const referrerData = referrerDoc.docs[0]?.data() || {};

        const expiryDate = new Date();
        expiryDate.setDate(expiryDate.getDate() + REFERRAL_CONFIG.EXPIRY_DAYS);

        await addDoc(collection(db, COLLECTIONS.REFERRALS), {
          referrerId,
          referrerName: referrerData.displayName || 'Müşteri',
          referrerEmail: referrerData.email || '',
          referredEmail: currentUser.email?.toLowerCase(),
          referredId: currentUser.uid,
          referredName: currentUser.displayName || 'Müşteri',
          status: 'registered',
          referralCode: code.toUpperCase(),
          rewardAmount: REFERRAL_CONFIG.REFERRER_REWARD,
          referrerRewarded: false,
          referredRewarded: false,
          createdAt: Timestamp.now(),
          registeredAt: Timestamp.now(),
          expiresAt: Timestamp.fromDate(expiryDate),
        });
      }

      return { success: true, message: `Referans kodu uygulandı! İlk siparişinizde ${REFERRAL_CONFIG.REFERRED_REWARD}₺ indirim kazandınız.` };
    } catch (error) {
      logger.error('Referans kodu uygulama hatası:', error);
      return { success: false, message: 'Kod uygulanamadı. Lütfen tekrar deneyin.' };
    }
  }, [currentUser]);

  // Referans kodu oluştur
  const generateReferralCode = useCallback(async (): Promise<string> => {
    if (!currentUser) return '';
    
    if (myReferralCode) return myReferralCode;

    const newCode = generateUniqueCode(currentUser.uid);
    try {
      await addDoc(collection(db, COLLECTIONS.REFERRAL_CODES), {
        userId: currentUser.uid,
        code: newCode,
        createdAt: Timestamp.now(),
      });
      setMyReferralCode(newCode);
      return newCode;
    } catch (error) {
      logger.error('Kod oluşturma hatası:', error);
      return '';
    }
  }, [currentUser, myReferralCode]);

  // Referans bakiyesi kullan
  const useReferralBalance = useCallback(async (amount: number): Promise<boolean> => {
    if (!currentUser) return false;
    
    if (amount > stats.availableBalance) {
      return false;
    }

    try {
      // Bakiye kullanımını kaydet
      await addDoc(collection(db, 'referralUsage'), {
        userId: currentUser.uid,
        amount,
        usedAt: Timestamp.now(),
      });
      
      return true;
    } catch (error) {
      logger.error('Bakiye kullanma hatası:', error);
      return false;
    }
  }, [currentUser, stats.availableBalance]);

  // Referans koduna göre bul
  const getReferralByCode = useCallback(async (code: string): Promise<Referral | null> => {
    try {
      const snap = await getDocs(
        query(collection(db, COLLECTIONS.REFERRALS), where('referralCode', '==', code.toUpperCase()))
      );
      
      if (snap.empty) return null;
      
      const d = snap.docs[0];
      return {
        id: d.id,
        ...d.data(),
      } as Referral;
    } catch (error) {
      logger.error('Referans arama hatası:', error);
      return null;
    }
  }, []);

  // Referansı tamamlandı olarak işaretle (admin)
  const markReferralCompleted = useCallback(async (referralId: string): Promise<void> => {
    try {
      await updateDoc(doc(db, COLLECTIONS.REFERRALS, referralId), {
        status: 'completed',
        referrerRewarded: true,
        referredRewarded: true,
        completedAt: Timestamp.now(),
      });
    } catch (error) {
      logger.error('Referans tamamlama hatası:', error);
    }
  }, []);

  const value = useMemo(() => ({
    referrals,
    stats,
    myReferralCode,
    loading,
    sendInvite,
    applyReferralCode,
    generateReferralCode,
    useReferralBalance,
    getReferralByCode,
    markReferralCompleted,
  }), [
    referrals,
    stats,
    myReferralCode,
    loading,
    sendInvite,
    applyReferralCode,
    generateReferralCode,
    useReferralBalance,
    getReferralByCode,
    markReferralCompleted,
  ]);

  return (
    <ReferralContext.Provider value={value}>
      {children}
    </ReferralContext.Provider>
  );
};

export default ReferralContext;
